/**
 * The buzzer's sound: a synthesised referee's whistle through Web Audio, no asset files.
 * Browsers only allow sound after one user gesture, so the projector calls unlock() on its
 * first click and remembers that in localStorage.
 */

const SOUND_KEY = "bd.sound";

let ctx: AudioContext | null = null;

export function soundRemembered(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) === "1";
  } catch {
    return false;
  }
}

/** Call from a click: makes (or wakes) the audio context and remembers the choice. */
export function unlock(): void {
  ctx ??= new AudioContext();
  void ctx.resume();
  try {
    localStorage.setItem(SOUND_KEY, "1");
  } catch {
    /* private window: the badge comes back next time */
  }
}

/** One tone of the whistle: a high pitch with a fast warble, shaped so it does not click. */
function tone(c: AudioContext, start: number, length: number): void {
  const osc = c.createOscillator();
  const warble = c.createOscillator();
  const depth = c.createGain();
  const gain = c.createGain();
  osc.type = "sine";
  osc.frequency.value = 2300;
  warble.frequency.value = 28;
  depth.gain.value = 120;
  warble.connect(depth).connect(osc.frequency);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(0.5, start + 0.02);
  gain.gain.setValueAtTime(0.5, start + length - 0.04);
  gain.gain.linearRampToValueAtTime(0, start + length);
  osc.connect(gain).connect(c.destination);
  osc.start(start);
  warble.start(start);
  osc.stop(start + length);
  warble.stop(start + length);
}

/** The audio context, once the page has been clicked; null while sound is still locked. */
function ready(): AudioContext | null {
  if (!ctx) {
    if (!soundRemembered()) return null;
    ctx = new AudioContext();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/**
 * The whistle: two blasts (the end of a game, a twist), or one short one for the start of a
 * round. Silent until the page has been clicked once.
 */
export function whistle(blasts: 1 | 2 = 2): void {
  const c = ready();
  if (!c) return;
  const t0 = c.currentTime + 0.05;
  if (blasts === 1) {
    tone(c, t0, 0.35);
    return;
  }
  tone(c, t0, 0.28);
  tone(c, t0 + 0.42, 0.5);
}

/* ---- the podium (decision 43): a drum roll into each place, a fanfare for the winner ---- */

/** White noise for `seconds`: the skin of the drum and the cymbal. */
function noise(c: AudioContext, seconds: number): AudioBuffer {
  const b = c.createBuffer(1, Math.ceil(c.sampleRate * seconds), c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i += 1) d[i] = Math.random() * 2 - 1;
  return b;
}

/** One stroke of noise through a band: a snare tap, or with a long tail a cymbal. */
function stroke(c: AudioContext, buffer: AudioBuffer, at: number, level: number, centre: number, tail: number, out: AudioNode = c.destination): void {
  const src = c.createBufferSource();
  src.buffer = buffer;
  const band = c.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = centre;
  band.Q.value = 0.7;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(level, at + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.001, at + tail);
  src.connect(band).connect(gain).connect(out);
  src.start(at);
  src.stop(at + tail + 0.02);
}

/** A brass-ish note: a sawtooth with its top taken off, shaped so it does not click. */
function note(c: AudioContext, at: number, freq: number, length: number, level: number, out: AudioNode = c.destination, type: OscillatorType = "sawtooth", top = 2600): void {
  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const soft = c.createBiquadFilter();
  soft.type = "lowpass";
  soft.frequency.value = top;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(level, at + 0.02);
  gain.gain.setValueAtTime(level, at + length - 0.08);
  gain.gain.linearRampToValueAtTime(0, at + length);
  osc.connect(soft).connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + length + 0.02);
}

/** A snare roll for `seconds`, quickening and growing, up to the place it leads into. */
export function drumRoll(seconds: number): void {
  const c = ready();
  if (!c) return;
  const skin = noise(c, 0.2);
  const t0 = c.currentTime + 0.05;
  for (let t = t0; t < t0 + seconds; ) {
    const k = (t - t0) / seconds;
    stroke(c, skin, t, 0.12 + 0.3 * k, 1900, 0.09);
    t += 0.075 - 0.035 * k;
  }
}

/** A place shown: one chord and a short crash. */
export function sting(): void {
  const c = ready();
  if (!c) return;
  const t0 = c.currentTime + 0.05;
  for (const f of [392, 523.25, 659.25]) note(c, t0, f, 0.55, 0.1);
  stroke(c, noise(c, 0.2), t0, 0.3, 5000, 0.5);
}

/** The winner: three notes up and a held chord, with a cymbal. */
export function fanfare(): void {
  const c = ready();
  if (!c) return;
  const t0 = c.currentTime + 0.05;
  note(c, t0, 392, 0.16, 0.18);
  note(c, t0 + 0.18, 523.25, 0.16, 0.18);
  note(c, t0 + 0.36, 659.25, 0.16, 0.18);
  for (const f of [523.25, 659.25, 783.99, 1046.5]) note(c, t0 + 0.56, f, 1.6, 0.09);
  stroke(c, noise(c, 0.2), t0 + 0.56, 0.35, 6000, 1.4);
}

/* ---- the reveal stepped from the owner's phone (decision 53): an original anthem, no files ---- */

/** Everything of one piece through a compressor, so the layers together never clip. */
function bus(c: AudioContext, level: number): GainNode {
  const squeeze = c.createDynamicsCompressor();
  squeeze.threshold.value = -14;
  squeeze.ratio.value = 4;
  const gain = c.createGain();
  gain.gain.value = level;
  gain.connect(squeeze).connect(c.destination);
  return gain;
}

/** A timpani stroke: a low sine that drops a little in pitch, and a soft thud of noise. */
function timpani(c: AudioContext, at: number, freq: number, level: number, out: AudioNode): void {
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq * 1.06, at);
  osc.frequency.exponentialRampToValueAtTime(freq, at + 0.12);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(level, at + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 1.4);
  osc.connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + 1.5);
  stroke(c, noise(c, 0.3), at, level * 0.4, 180, 0.25, out);
}

const HZ: Record<string, number> = {
  F2: 87.31, G2: 98, A2: 110, C3: 130.81,
  B3: 246.94, C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392, A4: 440, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880, B5: 987.77, C6: 1046.5,
};

/**
 * The tune: [note, beats] a bar at a time, with the bar's chord and bass. Written for the day,
 * in the grand brass manner of a podium anthem; it borrows nobody's melody.
 */
const ANTHEM: { tune: [string, number][]; chord: string[]; bass: string; half?: { chord: string[]; bass: string } }[] = [
  { tune: [["G4", 1], ["C5", 1], ["E5", 1.5], ["D5", 0.5]], chord: ["C4", "E4", "G4"], bass: "C3" },
  { tune: [["C5", 1], ["E5", 1], ["G5", 2]], chord: ["C4", "E4", "G4"], bass: "C3" },
  { tune: [["A5", 1.5], ["G5", 0.5], ["F5", 1], ["E5", 1]], chord: ["C4", "F4", "A4"], bass: "F2" },
  { tune: [["D5", 3], ["G4", 1]], chord: ["B3", "D4", "G4"], bass: "G2" },
  { tune: [["C5", 1], ["E5", 1], ["G5", 1.5], ["F5", 0.5]], chord: ["C4", "E4", "G4"], bass: "C3" },
  { tune: [["E5", 1], ["A5", 1], ["C6", 2]], chord: ["C4", "E4", "A4"], bass: "A2" },
  { tune: [["A5", 1], ["F5", 1], ["G5", 1], ["D5", 1]], chord: ["C4", "F4", "A4"], bass: "F2", half: { chord: ["B3", "D4", "G4"], bass: "G2" } },
  { tune: [["C6", 4]], chord: ["C4", "E4", "G4", "C5"], bass: "C3" },
];

/** The anthem playing now, so Back or taking the reveal off the wall can stop it. */
let playing: GainNode | null = null;

/** Stops the anthem, if it is playing: a quick fade, then the notes still to come go nowhere. */
export function hush(): void {
  const c = ctx;
  const g = playing;
  playing = null;
  if (!c || !g) return;
  const t = c.currentTime;
  g.gain.cancelScheduledValues(t);
  g.gain.setValueAtTime(g.gain.value, t);
  g.gain.linearRampToValueAtTime(0, t + 0.3);
  setTimeout(() => g.disconnect(), 400);
}

/** The winner's anthem: eight bars of brass over chords, bass and timpani, about 26 seconds. */
export function anthem(): void {
  const c = ready();
  if (!c) return;
  hush();
  const out = bus(c, 0.9);
  playing = out;
  const beat = 60 / 84;
  const t0 = c.currentTime + 0.08;
  const crash = noise(c, 0.3);
  stroke(c, crash, t0, 0.3, 6000, 2.2, out);
  ANTHEM.forEach((bar, n) => {
    const b0 = t0 + n * 4 * beat;
    const last = n === ANTHEM.length - 1;
    const len = (last ? 6 : 4) * beat;
    // the chord and the bass: a whole bar, or two halves
    const halves = bar.half ? [{ chord: bar.chord, bass: bar.bass, at: b0, len: 2 * beat }, { ...bar.half, at: b0 + 2 * beat, len: 2 * beat }] : [{ chord: bar.chord, bass: bar.bass, at: b0, len }];
    for (const h of halves) {
      for (const name of h.chord) note(c, h.at, HZ[name]!, h.len, 0.035, out, "sawtooth", 1500);
      note(c, h.at, HZ[h.bass]!, h.len, 0.09, out, "triangle", 900);
      timpani(c, h.at, HZ[h.bass]! / 2, 0.35, out);
    }
    if (n % 2 === 1 && !last) timpani(c, b0 + 3 * beat, HZ[bar.bass]! / 2, 0.22, out);
    // the tune, in brass, doubled an octave down
    let at = b0;
    for (const [name, beats] of bar.tune) {
      const hold = (last ? 6 : beats) * beat;
      note(c, at, HZ[name]!, hold * 0.93, 0.15, out, "sawtooth", 3200);
      note(c, at, HZ[name]! / 2, hold * 0.93, 0.05, out, "sawtooth", 2000);
      at += beats * beat;
    }
    if (last) {
      stroke(c, crash, b0, 0.35, 6000, 3, out);
      for (let k = 0; k < 10; k += 1) timpani(c, b0 + 4.5 * beat + k * 0.07, HZ.C3! / 2, 0.12 + k * 0.025, out);
      timpani(c, b0 + 4.5 * beat + 0.75, HZ.C3! / 2, 0.45, out);
    }
  });
}

/** An award handed out: the anthem's first four notes, quick, into a held chord. About two seconds. */
export function flourish(): void {
  const c = ready();
  if (!c) return;
  const out = bus(c, 0.9);
  const t0 = c.currentTime + 0.05;
  const steps: [string, number][] = [["G4", 0.14], ["C5", 0.14], ["E5", 0.22], ["D5", 0.12]];
  let at = t0;
  for (const [name, len] of steps) {
    note(c, at, HZ[name]!, len * 0.9, 0.16, out);
    at += len;
  }
  for (const name of ["C4", "E4", "G4", "C5", "E5"]) note(c, at, HZ[name]!, 1.3, 0.06, out, "sawtooth", 2400);
  timpani(c, at, HZ.C3! / 2, 0.35, out);
  stroke(c, noise(c, 0.2), at, 0.3, 6000, 1.2, out);
}
